import {
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { stationControl, stationTile } from "src/design/stationControl";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import {
  InlineAlert,
  LicensePausedNotice,
  useLicenseSuspended,
} from "src/grading/components";
import CodeBoxes from "src/grading/components/CodeBoxes/CodeBoxes";
import { entryTitleSx } from "src/grading/layout/EntryFrame";
import type { ModificationKind } from "src/models";
import {
  CATEGORY_EVENTS,
  EVENT_LABEL,
  EVENT_UNIT,
  EntryDto,
  EntryMode,
  EntryResponse,
  EventId,
  Station,
} from "src/models/sessions";
import { drain, isLicenseSuspended, outbox } from "src/services/offline/queue";
import { StationHeader, StationSyncStatus } from "./parts/StationHeader";
import { StudentTile } from "./parts/StudentTile";

const TOKEN_KEY = "pft.station.token";
const SESSION_KEY = "pft.station.session";
const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const MODES: Partial<Record<EventId, EntryMode[]>> = {
  "pft-mile": ["run", "walk", "wheel"],
  "pft-pullups": ["pull-up", "chin-up"],
};
const TIMED: Partial<Record<EventId, number>> = {
  "pft-situps": 60,
  "pft-pushups": 60,
};
const MODIFICATIONS: ModificationKind[] = [
  "none",
  "modification",
  "adaptation",
  "alternative",
];
const RULE_DEFAULTS: Record<EventId, string> = {
  "pft-situps":
    "Count only correct reps. The clock keeps running during rests.",
  "pft-plank": "Enter the seconds the student holds good form.",
  "pft-mile": "Enter the finish time as m:ss and pick run, walk or wheel.",
  "pft-beep-20m": "Record each student's last completed lap.",
  "pft-beep-15m": "Record each student's last completed lap.",
  "pft-pushups":
    "Count only correct reps. The clock keeps running during rests.",
  "pft-pullups": "Count only correct reps and pick pull-up or chin-up.",
};
const uuid = () =>
  globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const baseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
const clock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;
const pillSx = { ...stationControl, px: 3.5, fontSize: 16 };
/** Station text fields and selects: the 48 px station height at body-size text. */
const fieldSx = {
  "& .MuiInputBase-root": { minHeight: stationControl.minHeight },
};

interface ScorerSession {
  id: string;
  name: string;
  date: string;
  status: string;
  stations: Station[];
}
interface StationStudent {
  studentId: string;
  firstName: string;
  lastInitial: string;
  group: number;
  plannedModification: ModificationKind;
  plannedEventId: string | null;
}
interface StationView {
  station: Station;
  eventConfig: {
    info?: { safetyLine?: string; gradingCriteria?: string[] };
    testing?: Record<string, unknown>;
  } | null;
  students: StationStudent[];
  entries: {
    id: string;
    studentId: string;
    value: number;
    mode?: string;
    attempt?: number;
    supersededBy?: string | null;
  }[];
}
interface Draft {
  value: string;
  mode?: EntryMode;
  modification: ModificationKind;
  note: string;
}
interface UiMessage {
  key: string;
  defaultValue: string;
}
interface HttpError {
  status?: number;
}

const scorerFetch = async <T,>(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<T> => {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "x-session-token": token,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok)
    throw Object.assign(new Error(`HTTP ${res.status}`), {
      status: res.status,
      data: await res.json().catch(() => ({})),
    });
  return (await res.json()).data as T;
};

const Eyebrow = ({ children }: { children: string }) => (
  <Typography
    variant="overline"
    component="p"
    sx={(theme) => ({
      m: 0,
      color: theme.palette.dangerInk,
      letterSpacing: ".12em",
      lineHeight: 1,
    })}
  >
    {children}
  </Typography>
);

const ICON_FILE: Record<string, string> = {
  "pft-situps": "situps",
  "pft-plank": "plank",
  "pft-mile": "mile",
  "pft-beep-20m": "beep",
  "pft-beep-15m": "beep",
  "pft-pushups": "pushups",
  "pft-pullups": "pullups",
};

/** Event pictogram in a blue-soft tile (design station tiles and score header). */
const StationIcon = ({ eventId, size }: { eventId: string; size: number }) => {
  const file = ICON_FILE[eventId];
  const mask = `url(/assets/events/${file}.svg) center / contain no-repeat`;
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: size >= 60 ? "18px" : `${radius.panel}px`,
        bgcolor: "primary.light",
        display: "grid",
        placeItems: "center",
      }}
    >
      {file ? (
        <Box
          component="span"
          sx={{
            width: Math.round(size * 0.66),
            height: Math.round(size * 0.66),
            bgcolor: "primaryInk",
            WebkitMask: mask,
            mask,
          }}
        />
      ) : null}
    </Box>
  );
};

/**
 * Browser station for volunteers on a Chromebook: join by code, pick a station, record each
 * student's value with the event's mode and modification. Entries queue in localStorage and
 * sync in batches; a revoked code or finalized session clears the token.
 */
const WebStationPage = () => {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(() =>
    window.localStorage.getItem(TOKEN_KEY),
  );
  const [session, setSession] = useState<ScorerSession | null>(() => {
    try {
      return JSON.parse(window.localStorage.getItem(SESSION_KEY) || "null");
    } catch {
      return null;
    }
  });
  // A scanned QR opens /station?code=ABC234.
  const [code, setCode] = useState(() =>
    (new URLSearchParams(window.location.search).get("code") ?? "")
      .toUpperCase()
      .slice(0, 6),
  );
  const [displayName, setDisplayName] = useState("");
  const [stationId, setStationId] = useState<string | null>(null);
  const [view, setView] = useState<StationView | null>(null);
  const [error, setError] = useState<UiMessage | null>(null);
  const [closed, setClosed] = useState(false);
  // FR-043: entries are refused while the district's license is suspended; they stay queued.
  const ownLicenseSuspended = useLicenseSuspended();
  const [licenseRefused, setLicenseRefused] = useState(false);
  const [pending, setPending] = useState(outbox.count());
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [timer, setTimer] = useState<{
    startedAt: number;
    duration: number;
  } | null>(null);
  const [, tick] = useState(0);
  const [group, setGroup] = useState<number | null>(null);
  const [submittedGroup, setSubmittedGroup] = useState<number | null>(null);

  useEffect(() => {
    document.title = `${t("station.title", { defaultValue: "Station" })} · ${t("app.name")}`;
  }, [t]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  const leave = useCallback(() => {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(SESSION_KEY);
    if (session) outbox.purgeSession(session.id);
    setToken(null);
    setSession(null);
    setStationId(null);
    setView(null);
    setClosed(false);
    setTimer(null);
    setGroup(null);
    setSubmittedGroup(null);
  }, [session]);

  const sync = useCallback(async () => {
    if (!token) return;
    try {
      await drain(
        async (sessionId, entries) =>
          (
            await scorerFetch<{ results: EntryResponse[] }>(
              `/api/grading/v1/sessions/${sessionId}/entries/batch`,
              token,
              { method: "POST", body: JSON.stringify({ entries }) },
            )
          ).results,
      );
      setLicenseRefused(false);
    } catch (err) {
      if (isLicenseSuspended(err)) setLicenseRefused(true);
      else setClosed(true);
    }
    setPending(outbox.count());
  }, [token]);

  useEffect(() => {
    window.addEventListener("online", sync);
    const id = setInterval(sync, 5000);
    return () => {
      window.removeEventListener("online", sync);
      clearInterval(id);
    };
  }, [sync]);

  useEffect(() => {
    if (!token || !session || !stationId) return;
    let cancelled = false;
    const load = () =>
      scorerFetch<StationView>(
        `/api/grading/v1/sessions/${session.id}/stations/${stationId}`,
        token,
      )
        .then((v) => !cancelled && setView(v))
        .catch((err) =>
          err?.status === 403 || err?.status === 401 || err?.status === 409
            ? setClosed(true)
            : setError({
                key: "station.loadError",
                defaultValue: "Could not load the station. Retrying…",
              }),
        );
    load();
    const id = setInterval(load, 10_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, session, stationId]);

  useEffect(() => {
    if (!timer) return;
    const id = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [timer]);

  const join = async () => {
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/api/grading/v1/sessions/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: code.toUpperCase(),
          displayName: displayName || undefined,
        }),
      });
      if (!res.ok)
        throw Object.assign(new Error("join"), { status: res.status });
      const { data } = await res.json();
      window.localStorage.setItem(TOKEN_KEY, data.sessionToken);
      window.localStorage.setItem(SESSION_KEY, JSON.stringify(data.session));
      setToken(data.sessionToken);
      setSession(data.session);
    } catch (err: unknown) {
      const status = (err as HttpError | null)?.status;
      setError(
        status === 429
          ? {
              key: "station.join.tooMany",
              defaultValue: "Too many attempts. Wait a minute and try again.",
            }
          : status === 401
            ? {
                key: "station.join.inactiveCode",
                defaultValue:
                  "That code is not active. Ask the teacher for the current code.",
              }
            : {
                key: "station.join.networkError",
                defaultValue: "Could not join. Check your connection.",
              },
      );
    }
  };

  const remaining = timer
    ? Math.max(
        0,
        timer.duration - Math.floor((Date.now() - timer.startedAt) / 1000),
      )
    : null;
  const eventId = (view?.station.eventId ?? "pft-pushups") as EventId;
  const unit = EVENT_UNIT[eventId];
  const isCount = unit !== "seconds";
  const recorded = useMemo(
    () =>
      new Map(
        (view?.entries ?? [])
          .filter((e) => !e.supersededBy)
          .map((e) => [e.studentId, e]),
      ),
    [view],
  );

  /** Same validation and entry shape the per-student save used; `null` when not ready. */
  const buildEntry = (studentId: string): EntryDto | null => {
    if (!view) return null;
    const d = drafts[studentId];
    if (!d?.value) return null;
    const value =
      eventId === "pft-mile" && d.value.includes(":")
        ? Number(d.value.split(":")[0]) * 60 + Number(d.value.split(":")[1])
        : Number(d.value);
    if (isNaN(value)) return null;
    if (d.modification !== "none" && !d.note.trim()) return null;
    return {
      id: uuid(),
      stationId: view.station.id,
      studentId,
      eventId,
      value,
      unit,
      mode: d.mode ?? MODES[eventId]?.[0],
      modification: d.modification,
      modificationNote: d.note || undefined,
      recordedAt: new Date().toISOString(),
    };
  };

  /**
   * The page's per-student save: queue one entry in the outbox, show it as recorded and clear the
   * draft. Functional updates so "Submit group" can run it for several students in one click.
   */
  const queueEntry = (studentId: string): boolean => {
    if (!session || !view) return false;
    const entry = buildEntry(studentId);
    if (!entry) return false;
    outbox.enqueue(session.id, entry);
    setView((v) =>
      v
        ? {
            ...v,
            entries: [
              ...v.entries,
              { id: entry.id, studentId, value: entry.value, mode: entry.mode },
            ],
          }
        : v,
    );
    setDrafts((ds) => ({
      ...ds,
      [studentId]: { value: "", modification: "none", note: "" },
    }));
    return true;
  };

  const save = (studentId: string) => {
    if (!queueEntry(studentId)) return;
    setPending(outbox.count());
    void sync();
  };

  /** Convenience: runs the per-student save for every ready student, then syncs once. */
  const submitGroup = (studentIds: string[], g: number) => {
    const queued = studentIds.filter((id) => queueEntry(id)).length;
    if (queued === 0) return;
    setPending(outbox.count());
    setSubmittedGroup(g);
    void sync();
  };

  const syncStatus: StationSyncStatus | null =
    !token || !session
      ? null
      : !online
        ? "offline"
        : pending > 0
          ? "pending"
          : "synced";

  const shell = (content: ReactNode, onLeave?: () => void) => (
    <Box
      sx={(theme) => ({
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.background.default,
      })}
    >
      <StationHeader
        syncStatus={syncStatus}
        pending={pending}
        onLeave={onLeave}
      />
      <Box
        component="main"
        id="main"
        tabIndex={-1}
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: 1100,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
          display: "flex",
          flexDirection: "column",
          gap: 2.75,
          "&:focus": { outline: "none" },
        }}
      >
        {!online && token && session ? (
          <InlineAlert severity="warning">
            {t("station.offlineBanner", {
              count: pending,
              defaultValue:
                "You're offline. {{count}} entries are saved on this laptop and will sync when the connection returns.",
            })}
          </InlineAlert>
        ) : null}
        {token && session && (ownLicenseSuspended || licenseRefused) ? (
          <LicensePausedNotice />
        ) : null}
        {content}
      </Box>
    </Box>
  );

  if (!token || !session) {
    return shell(
      <Paper
        sx={{
          maxWidth: 520,
          width: "100%",
          mx: "auto",
          mt: { xs: 0, sm: 5 },
          borderRadius: "24px",
          p: { xs: 3, sm: 4 },
          display: "flex",
          flexDirection: "column",
          gap: 2.75,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Eyebrow>
            {t("station.join.eyebrow", {
              defaultValue: "Volunteers & partners",
            })}
          </Eyebrow>
          <Typography variant="h1" sx={entryTitleSx}>
            {t("station.join.title", { defaultValue: "Join a test day" })}
          </Typography>
          <Typography
            sx={{ fontSize: 14.5, lineHeight: 1.45, color: "text.secondary" }}
          >
            {t("station.join.helper", {
              defaultValue:
                "Type the 6-character code from the teacher's screen. You'll only see first names and last initials.",
            })}
          </Typography>
        </Box>
        <CodeBoxes
          length={6}
          value={code}
          uppercase
          autoFocus
          aria-label={t("station.join.codeLabel", {
            defaultValue: "Join code",
          })}
          onChange={(next) =>
            setCode(
              next
                .toUpperCase()
                .split("")
                .filter((c) => ALPHABET.includes(c))
                .join("")
                .slice(0, 6),
            )
          }
        />
        <TextField
          label={t("join.namePlaceholder")}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          inputProps={{ maxLength: 40 }}
          fullWidth
        />
        {error ? (
          <InlineAlert severity="error">
            {t(error.key, { defaultValue: error.defaultValue })}
          </InlineAlert>
        ) : null}
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={code.length !== 6}
          onClick={join}
          sx={{ ...pillSx, minHeight: 56, fontSize: 16 }}
        >
          {t("station.join.submit", { defaultValue: "Join session" })}
        </Button>
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {t("station.join.urlHint", {
            url: `${window.location.origin}/station`,
            defaultValue: "Station page: {{url}}",
          })}
        </Typography>
      </Paper>,
    );
  }

  if (closed) {
    return shell(
      <Box sx={{ maxWidth: 560, width: "100%", mx: "auto", mt: 5 }}>
        <InlineAlert
          severity="info"
          action={
            <Button variant="outlined" onClick={leave} sx={pillSx}>
              {t("station.joinAnother", {
                defaultValue: "Join another session",
              })}
            </Button>
          }
        >
          {t("station.sessionClosed")}
        </InlineAlert>
      </Box>,
      leave,
    );
  }

  if (!stationId) {
    return shell(
      <>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Eyebrow>{session.name}</Eyebrow>
          <Typography variant="h1" sx={entryTitleSx}>
            {t("station.pickTitle", { defaultValue: "Pick your station" })}
          </Typography>
        </Box>
        <Box
          component="ul"
          sx={{
            m: 0,
            p: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 2,
          }}
        >
          {session.stations.map((s) => (
            <Box component="li" key={s.id} sx={{ listStyle: "none" }}>
              <Paper
                component="button"
                type="button"
                disabled={s.locked}
                onClick={() => setStationId(s.id)}
                sx={(theme) => ({
                  ...stationTile,
                  width: "100%",
                  minHeight: 220,
                  borderRadius: "22px",
                  p: 3,
                  border: 0,
                  font: "inherit",
                  color: theme.palette.text.primary,
                  textAlign: "left",
                  cursor: s.locked ? "default" : "pointer",
                  opacity: s.locked ? 0.6 : 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1.75,
                  transition: "transform .2s",
                  "&:hover": s.locked
                    ? undefined
                    : { transform: "translateY(-2px)" },
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.focusRing}`,
                    outlineOffset: 2,
                  },
                })}
              >
                <StationIcon eventId={s.eventId} size={64} />
                <Typography
                  variant="overline"
                  component="span"
                  sx={{ color: "dangerInk", lineHeight: 1 }}
                >
                  {t(`categories.${s.category}`)}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 22,
                    fontWeight: 900,
                    lineHeight: 1.1,
                    fontStretch: fontStretch.heading,
                  }}
                >
                  {s.label}
                </Typography>
                <Typography
                  component="span"
                  sx={{ mt: "auto", fontSize: 13.5, color: "text.secondary" }}
                >
                  {s.locked
                    ? t("station.stationLocked")
                    : t(`station.rule.${s.eventId}`, {
                        defaultValue:
                          RULE_DEFAULTS[s.eventId as EventId] ??
                          EVENT_LABEL[s.eventId as EventId] ??
                          "",
                      })}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </>,
      leave,
    );
  }

  const students = view?.students ?? [];
  const groups = Array.from(new Set(students.map((s) => s.group))).sort(
    (a, b) => a - b,
  );
  const firstOpen = students.find((s) => !recorded.has(s.studentId))?.group;
  const g =
    group !== null && groups.includes(group)
      ? group
      : (firstOpen ?? groups[0] ?? 1);
  const groupStudents = students.filter((s) => s.group === g);
  const nextGroup = groups.find((n) => n > g);
  const ready = groupStudents.filter(
    (s) => !recorded.has(s.studentId) && buildEntry(s.studentId) !== null,
  );
  const unrecorded = groupStudents.filter(
    (s) => !recorded.has(s.studentId),
  ).length;
  const groupDone = submittedGroup === g && unrecorded === 0;
  const valueLabel =
    eventId === "pft-mile"
      ? t("station.timeLabel", { defaultValue: "Time (m:ss)" })
      : unit;
  const timeUp = remaining === 0;
  const timed = TIMED[eventId];
  const rule =
    view?.eventConfig?.info?.gradingCriteria?.join(" ") ||
    t(`station.rule.${eventId}`, { defaultValue: RULE_DEFAULTS[eventId] });

  return shell(
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => {
              setStationId(null);
              setTimer(null);
              setGroup(null);
              setSubmittedGroup(null);
            }}
            sx={{
              ...stationControl,
              alignSelf: "flex-start",
              fontSize: 14,
              color: "text.secondary",
              px: 1,
            }}
          >
            <span aria-hidden="true">←&nbsp;</span>
            {t("station.changeStation", { defaultValue: "Change station" })}
          </Button>
          <Box sx={{ display: "flex", gap: 1.75, alignItems: "center" }}>
            <StationIcon eventId={eventId} size={52} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Eyebrow>
                {t("station.groupHeader", {
                  group: g,
                  count: groupStudents.length,
                  defaultValue: "Group {{group}} · {{count}} students",
                })}
              </Eyebrow>
              <Typography
                variant="h1"
                sx={{ fontSize: 28, textTransform: "uppercase", lineHeight: 1 }}
              >
                {EVENT_LABEL[eventId]}
              </Typography>
            </Box>
          </Box>
        </Box>
        {timed ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              aria-hidden
              data-testid="timer-ring"
              sx={(theme) => {
                const total = timer?.duration ?? timed;
                const left = remaining ?? total;
                const deg = total > 0 ? Math.round((left / total) * 360) : 0;
                const tone =
                  left <= 10
                    ? theme.palette.error.main
                    : theme.palette.primary.main;
                return {
                  width: 96,
                  height: 96,
                  flex: "none",
                  borderRadius: "50%",
                  background: `conic-gradient(${tone} ${deg}deg, ${theme.palette.track} 0)`,
                  display: "grid",
                  placeItems: "center",
                };
              }}
            >
              <Typography
                component="p"
                role="timer"
                aria-hidden="true"
                sx={(theme) => ({
                  m: 0,
                  width: 78,
                  height: 78,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: theme.palette.background.default,
                  fontSize: timeUp ? 18 : 24,
                  fontWeight: 800,
                  fontStretch: fontStretch.condensed,
                  textAlign: "center",
                  color: timeUp ? theme.palette.dangerInk : "inherit",
                })}
              >
                {remaining === null
                  ? clock(timed)
                  : timeUp
                    ? t("station.timeUp")
                    : clock(remaining)}
              </Typography>
            </Box>
            <Box component="span" role="status" sx={srOnly}>
              {remaining === null
                ? ""
                : timeUp
                  ? t("station.timeUp")
                  : remaining > 10
                    ? clock(timer?.duration ?? timed)
                    : clock(remaining)}
            </Box>
            {!timer ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={() =>
                  setTimer({ startedAt: Date.now(), duration: timed })
                }
                sx={{ ...pillSx, minHeight: 64, fontSize: 16, px: 3.25 }}
              >
                {t("station.startTimer")}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setTimer(null)}
                sx={{ ...pillSx, minHeight: 64, fontSize: 16, px: 3.25 }}
              >
                {timeUp ? t("station.resetClock") : t("station.stopTimer")}
              </Button>
            )}
          </Box>
        ) : null}
      </Box>
      <Box
        sx={(theme) => ({
          px: 2,
          py: 1.5,
          borderRadius: "14px",
          bgcolor: theme.palette.surfaceAlt,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        })}
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{rule}</Typography>
        {view?.eventConfig?.info?.safetyLine ? (
          <Typography
            sx={(theme) => ({
              fontSize: 13.5,
              fontWeight: 700,
              color: theme.palette.dangerInk,
            })}
          >
            {view.eventConfig.info.safetyLine}
          </Typography>
        ) : null}
      </Box>
      {error ? (
        <InlineAlert severity="warning">
          {t(error.key, { defaultValue: error.defaultValue })}
        </InlineAlert>
      ) : null}
      {CATEGORY_EVENTS.cardio.includes(eventId) && eventId !== "pft-mile" ? (
        <InlineAlert severity="info">
          {t("station.beepNotice", {
            defaultValue:
              "The beep-test audio plays on the teacher's device; record each student's last completed lap here.",
          })}
        </InlineAlert>
      ) : null}
      {groups.length > 1 ? (
        <ToggleButtonGroup
          exclusive
          value={g}
          onChange={(_e, next: number | null) => {
            if (next === null) return;
            setGroup(next);
            setSubmittedGroup(null);
          }}
          aria-label={t("station.pickGroup", { defaultValue: "Group" })}
          sx={{ flexWrap: "wrap" }}
        >
          {groups.map((n) => (
            <ToggleButton key={n} value={n} sx={{ minHeight: 48, px: 2 }}>
              {t("station.group", { group: n })}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : null}
      {timeUp ? (
        <Box
          role="alert"
          sx={(theme) => ({
            p: "14px 18px",
            borderRadius: "14px",
            bgcolor: theme.palette.error.main,
            color: theme.palette.onTier,
            fontSize: 20,
            fontWeight: 900,
            fontStretch: fontStretch.display,
            textTransform: "uppercase",
            textAlign: "center",
          })}
        >
          {t("station.timeUpBanner", {
            defaultValue: "Time! Check the counts, then submit.",
          })}
        </Box>
      ) : null}
      {submittedGroup === g && unrecorded > 0 ? (
        <InlineAlert severity="info">
          {t("station.someNotRecorded", {
            count: unrecorded,
            defaultValue:
              "{{count}} students in this group still need a score.",
          })}
        </InlineAlert>
      ) : null}
      {groupDone ? (
        <Box
          role="status"
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.75,
            px: 2.75,
            py: 2.25,
            borderRadius: "18px",
            bgcolor: theme.palette.success.light,
          })}
        >
          <Typography
            sx={(theme) => ({
              fontSize: 16,
              fontWeight: 800,
              color: theme.palette.successInk,
            })}
          >
            {t("station.groupRecorded", {
              group: g,
              defaultValue: "Group {{group}} recorded.",
            })}
          </Typography>
          {nextGroup !== undefined ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setGroup(nextGroup);
                setSubmittedGroup(null);
                setTimer(null);
              }}
              sx={pillSx}
            >
              {t("station.startGroup", {
                group: nextGroup,
                defaultValue: "Start group {{group}}",
              })}
            </Button>
          ) : null}
        </Box>
      ) : null}
      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 1.75,
        }}
      >
        {groupStudents.map((s) => {
          const done = recorded.get(s.studentId);
          const d: Draft = drafts[s.studentId] ?? {
            value: "",
            modification: s.plannedModification ?? "none",
            note: "",
          };
          const setDraft = (next: Partial<Draft>) =>
            setDrafts((ds) => ({
              ...ds,
              [s.studentId]: {
                ...(ds[s.studentId] ?? d),
                ...next,
              },
            }));
          const bump = (delta: number) =>
            setDrafts((ds) => {
              const cur = ds[s.studentId] ?? d;
              const next = Math.max(0, (Number(cur.value) || 0) + delta);
              return { ...ds, [s.studentId]: { ...cur, value: String(next) } };
            });
          const name = `${s.firstName} ${s.lastInitial}.`;
          const count = Number(d.value) || 0;
          return (
            <StudentTile
              key={s.studentId}
              name={name}
              plannedLabel={
                s.plannedModification && s.plannedModification !== "none"
                  ? t(`modification.${s.plannedModification}`)
                  : undefined
              }
              recordedText={
                done
                  ? `${t("station.recorded", { value: done.value })} ${unit}${
                      done.mode ? ` (${done.mode})` : ""
                    }`
                  : undefined
              }
              count={isCount ? count : undefined}
              onPlus={() => bump(1)}
              onUndo={() => bump(-1)}
            >
              {!isCount ? (
                <TextField
                  label={valueLabel}
                  value={d.value}
                  onChange={(e) => setDraft({ value: e.target.value })}
                  inputProps={{
                    inputMode: "numeric",
                    "aria-label": `${valueLabel}, ${name}`,
                  }}
                  sx={fieldSx}
                />
              ) : null}
              {MODES[eventId] ? (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={d.mode ?? MODES[eventId]?.[0]}
                  onChange={(_e, mode: EntryMode | null) =>
                    mode && setDraft({ mode })
                  }
                  aria-label={t("station.modeFor", {
                    defaultValue: "Mode, {{name}}",
                    name,
                  })}
                  sx={{ flexWrap: "wrap" }}
                >
                  {(MODES[eventId] ?? []).map((m) => (
                    <ToggleButton key={m} value={m} sx={{ minHeight: 48 }}>
                      {t(`station.mode.${m}`)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : null}
              <TextField
                select
                label={t("modification.label")}
                sx={fieldSx}
                value={d.modification}
                onChange={(e) =>
                  setDraft({
                    modification: e.target.value as ModificationKind,
                  })
                }
                SelectProps={{
                  SelectDisplayProps: {
                    "aria-label": `${t("modification.label")}, ${name}`,
                  },
                }}
              >
                {MODIFICATIONS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {t(`modification.${m}`)}
                  </MenuItem>
                ))}
              </TextField>
              {d.modification !== "none" ? (
                <TextField
                  sx={fieldSx}
                  label={t("station.noteRequiredLabel", {
                    defaultValue: "Note (required)",
                  })}
                  value={d.note}
                  onChange={(e) => setDraft({ note: e.target.value })}
                  error={!d.note.trim()}
                  helperText={
                    !d.note.trim() ? t("modification.noteRequired") : undefined
                  }
                />
              ) : null}
              <Button
                variant="contained"
                color="secondary"
                onClick={() => save(s.studentId)}
                disabled={
                  !d.value || (d.modification !== "none" && !d.note.trim())
                }
                aria-label={t("station.saveFor", {
                  name,
                  defaultValue: "Save for {{name}}",
                })}
                sx={{ ...stationControl, fontSize: 16 }}
              >
                {t("common.save")}
              </Button>
            </StudentTile>
          );
        })}
      </Box>
      {view && students.length === 0 ? (
        <Typography color="text.secondary">
          {t("station.noStudents", {
            defaultValue: "No students assigned to this station.",
          })}
        </Typography>
      ) : null}
      {view && groupStudents.length > 0 && !groupDone ? (
        <Box
          sx={(theme) => ({
            position: "sticky",
            bottom: 0,
            mx: { xs: -2, sm: -3 },
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            bgcolor: theme.palette.background.default,
            borderTop: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Button
            variant="contained"
            disabled={ready.length === 0}
            onClick={() =>
              submitGroup(
                ready.map((s) => s.studentId),
                g,
              )
            }
            sx={{ ...pillSx, minHeight: 64, fontSize: 16, px: 3.75 }}
          >
            {t("station.submitGroup", { count: ready.length })}
          </Button>
        </Box>
      ) : null}
    </>,
    leave,
  );
};

export default WebStationPage;
